<template>
  <div
    v-if="shouldShowBanner"
    class="monthly-to-yearly-promo-container"
  >
    <BasePromotion
      variant="primary"
      :promotionName="'monthly-to-yearly'"
      :content="promotionContent"
      :ariaLabel="$i18nPresenter.t('See the deal')"
      :actionsLinkText="$i18nPresenter.t('See the deal')"
      :actionsLinkAriaLabel="$i18nPresenter.t('See the deal')"
      :closeAriaLabel="$i18nPresenter.t('Close')"
      :fullClickable="true"
      :actionDisabled="isUpgradingFromMonthlyToYearly"
      :actionLoading="isUpgradingFromMonthlyToYearly"
      @actions-link-click="handleActionsLinkClick"
      @close="handleClose"
      @loaded="handleLoaded"
    >
    </BasePromotion>
  </div>
</template>

<script>
import BasePromotion from '@/components-v2/common/global-notification/BasePromotion.vue'
import { mapGetters, mapActions, mapState } from 'vuex'
import util from '@/util'

export default {
  components: {
    BasePromotion,
  },
  data() {
    return {
      sessionStorageKey: 'monthly-to-yearly-banner-shown',
      localStorageKey: 'monthly-to-yearly-banner-dismiss-count',
      maxDismissCount: 3,
    }
  },
  computed: {
    ...mapState([
      'isUpgradingFromMonthlyToYearly',
      'isShowMonthlyToYearlyPromoBanner',
      'currentUser',
    ]),
    ...mapGetters(['monthlyToYearlyUpgradeAmount', 'shouldShowMonthlyToYearlyPromo']),
    // Create unique localStorage key for each user
    userLocalStorageKey() {
      const userId = this.currentUser?.id
      return userId ? `${this.localStorageKey}-${userId}` : this.localStorageKey
    },
    // Create unique sessionStorage key for each user
    userSessionStorageKey() {
      const userId = this.currentUser?.id
      return userId ? `${this.sessionStorageKey}-${userId}` : this.sessionStorageKey
    },
    promotionContent() {
      return this.$i18nPresenter.t(
        'Limited-time deal: Less than {amount}/month to unlock a full year of AhaSlides.',
        { amount: this.monthlyToYearlyUpgradeAmount },
      )
    },
    shouldShowBanner() {
      if (!this.shouldShowMonthlyToYearlyPromo) {
        return false
      }

      // Check if banner was dismissed forever
      const isDismissedForever = this.getDismissCount() >= this.maxDismissCount

      // Check if banner was shown in current session (combine sessionStorage + Vuex state)
      const isShownInSession = this.isBannerShownInSession() || !this.isShowMonthlyToYearlyPromoBanner

      return !isDismissedForever && !isShownInSession
    },
  },
  mounted() {
    // Reset banner state when starting a new session
    // This ensures banner can show again in new tabs/sessions (until dismissed 3 times)
    const isDismissedForever = this.getDismissCount() >= this.maxDismissCount
    if (!isDismissedForever) {
      this.setShowMonthlyToYearlyPromoBannerAction(true)
    }
  },
  methods: {
    ...mapActions(['upgradeFromMonthlyToYearlyAction', 'setShowMonthlyToYearlyPromoBannerAction']),
    isBannerShownInSession() {
      try {
        return sessionStorage.getItem(this.userSessionStorageKey) === 'true'
      } catch (error) {
        return false
      }
    },
    markBannerAsShown() {
      try {
        sessionStorage.setItem(this.userSessionStorageKey, 'true')
      } catch (error) {
        // do nothing
      }
    },
    getDismissCount() {
      try {
        const stored = localStorage.getItem(this.userLocalStorageKey)
        return stored ? parseInt(stored, 10) : 0
      } catch (error) {
        return 0
      }
    },
    handleLoaded() {
      util.mixpanelTracking('Monthly To Yearly Banner', 'Banner Visible')
    },
    incrementDismissCount() {
      try {
        const currentCount = this.getDismissCount()
        const newCount = currentCount + 1
        localStorage.setItem(this.userLocalStorageKey, newCount.toString())
      } catch (error) {
        // do nothing
        return 0
      }
    },
    handleActionsLinkClick() {
      this.upgradeFromMonthlyToYearlyAction()
    },
    handleClose() {
      // Mark banner as shown in this session (sessionStorage)
      this.markBannerAsShown()

      // Hide banner in current session (Vuex state)
      this.setShowMonthlyToYearlyPromoBannerAction(false)

      // Increment dismiss count
      this.incrementDismissCount()
    },
  },
}
</script>
